<template>
  <validation-form @on-submit="submitForm">
    <div class="personal-information">
      <!-- MAIN_INFO -->
      <md-card class="card-content">
        <md-card-header class="md-card-header-text md-card-header-green">
          <div class="card-text">
            <h4 class="title text-lg">{{ $t("class.main_information") }}</h4>
          </div>
        </md-card-header>

        <md-card-content>
          <div class="md-layout">
            <!-- Class name -->
            <div class="md-layout-item md-size-33 md-small-size-100">
              <validation-field
                :validations="{ required: true }"
                name="class name"
                :field-class="getValidationClass('name')"
              >
                <label>{{ $t("class.class_name") }}</label>
                <md-input v-model.trim="form.name" required></md-input>
                <span class="md-error" v-if="serverValidations.name">{{
                  serverValidations.name
                }}</span>
              </validation-field>
            </div>
            <!-- Class name -->

            <!-- Terms -->
            <div class="md-layout-item md-size-33 md-small-size-100">
              <validation-field :validations="{ required: true }" name="term">
                <label>{{ $t("class.term") }}</label>
                <md-select
                  v-model="form.term_id"
                  name="term"
                  id="term"
                  required
                >
                  <md-option
                    v-for="(term, idx) in termOpts"
                    :value="term.id"
                    :key="'term' + idx"
                    required
                    >{{ term.name }}
                  </md-option>
                </md-select>
              </validation-field>
            </div>
            <!-- Terms -->

            <!-- Status -->
            <div class="md-layout-item md-size-33 md-small-size-100">
              <md-field>
                <label>{{ $t("common.status") }}</label>
                <md-select
                  v-model="form.status"
                  name="status"
                  id="status"
                  required
                >
                  <md-option
                    v-for="status in statusOpts"
                    :value="status.id"
                    :key="status.id"
                    required
                    >{{ $t(status.name) }}
                  </md-option>
                </md-select>
              </md-field>
            </div>
            <!-- Status -->

            <!-- start date -->
            <div class="md-layout-item md-size-33 md-small-size-100">
              <validation-field
                :validations="{ required: true }"
                name="start date"
                type="date-picker"
              >
                <template #default="{ errors }">
                  <date-picker-custom
                    :label="$t('common.start_date')"
                    v-model="form.start_date"
                    required
                  >
                    <span class="md-error">{{ errors[0] }}</span>
                  </date-picker-custom>
                </template>
              </validation-field>
            </div>
            <!-- start date -->

            <!-- End date -->
            <div class="md-layout-item md-size-33 md-small-size-100">
              <validation-field
                :validations="{ required: true }"
                name="start date"
                type="date-picker"
              >
                <template #default="{ errors }">
                  <date-picker-custom
                    :label="$t('common.end_date')"
                    v-model="form.end_date"
                    required
                  >
                    <span class="md-error">{{
                      errors[0] || invalidReleaseHireDateMsg
                    }}</span>
                  </date-picker-custom>
                </template>
              </validation-field>
            </div>
            <!-- End date -->

            <!-- Subject -->
            <div class="md-layout-item md-size-33 md-small-size-100">
              <validation-field
                :validations="{ required: true }"
                name="subject"
                type="select-filter"
              >
                <template #default="{ errors }">
                  <select-filter
                    v-model="subject.displayValue"
                    :label="subject.label"
                    :value.sync="subject.value"
                    :options="subject.options"
                    @get-options="fetchSubjectOptions($event)"
                    :class="{ 'md-invalid': !!errors.length }"
                    :disable="!canEditClassInfo"
                    required
                  >
                    <span class="md-error" style="top: 35px">{{
                      errors[0]
                    }}</span>
                  </select-filter>
                </template>
              </validation-field>
            </div>
            <!-- Subject -->

            <!-- LMS System-->
            <div class="md-layout-item md-size-33 md-small-size-100">
              <validation-field
                :validations="{ required: true }"
                name="LMS system"
              >
                <label>{{ $t("class.system") }}</label>
                <md-select
                  v-model="form.lms_id"
                  name="lms"
                  id="lms"
                  required
                  :disabled="isDetailPage && !allowEditing"
                >
                  <md-option
                    v-for="(lms, idx) in lmsOpts"
                    :value="lms.id"
                    :key="'lms' + idx"
                    >{{ lms.name }}
                  </md-option>
                </md-select>
              </validation-field>
            </div>
            <!-- LMS System-->

            <!-- LMS zone -->
            <div
              class="md-layout-item md-size-33 md-small-size-100"
              v-if="isExternalLMS"
            >
              <md-field v-if="isDetailPage && !allowEditing">
                <label>{{ getTitleZone() }}</label>
                <md-input v-model.trim="zoneDisplayValue" disabled></md-input>
              </md-field>

              <validation-field
                v-else
                :validations="{ required: isExternalLMS }"
                :name="getTitleZone()"
                type="autocomplete"
              >
                <template #default="{ errors }">
                  <isis-autocomplete
                    v-model="zoneDisplayValue"
                    :value.sync="form.zone"
                    :options.sync="lmsZoneOpts"
                    :refetchOptions="
                      queryObj => getLMSZoneOpts(form.lms_id, queryObj)
                    "
                    :fields="{ text: 'title', value: 'id' }"
                    :label="getTitleZone()"
                    search-key="title"
                    required
                    :class="{ 'md-invalid': !!errors.length }"
                    @update:value="selectedZone"
                  >
                    <template>
                      <span class="md-error">{{ errors[0] }}</span>
                    </template>
                  </isis-autocomplete>
                </template>
              </validation-field>
            </div>
            <!-- LMS zone -->

            <!-- LMS course name -->
            <div
              class="md-layout-item md-size-33 md-small-size-100"
              v-if="isExternalLMS"
            >
              <md-field v-if="isDetailPage && !allowEditing">
                <label>{{ getTitleCourseName() }}</label>
                <md-input v-model.trim="courseDisplayValue" disabled></md-input>
              </md-field>

              <validation-field
                v-else
                :validations="{ required: true }"
                :name="getTitleCourseName()"
                type="autocomplete"
              >
                <template #default="{ errors }">
                  <isis-autocomplete
                    v-model="courseDisplayValue"
                    :value.sync="form.course_id"
                    :options.sync="lmsCourseNameOpts"
                    :refetchOptions="
                      queryObj => selectedLMSName == 'IGS-LMS' ? filterLMSCourseOpts(queryObj) 
                      : getLMSCourseOpts(form.lms_id, queryObj)
                    "
                    :fields="{ text: 'name', value: 'id' }"
                    :label="getTitleCourseName()"
                    required
                    search-key="name"
                    :class="{ 'md-invalid': !!errors.length }"
                  >
                    <template>
                      <span class="md-error">{{ errors[0] }}</span>
                    </template>
                  </isis-autocomplete>
                </template>
              </validation-field>
            </div>
            <!-- LMS course name -->

            <!-- Description -->
            <div class="md-layout-item md-size-67 md-small-size-100">
              <md-field>
                <label>{{ $t("common.description") }}</label>
                <md-input v-model.trim="form.description"></md-input>
              </md-field>
            </div>
            <!-- Description -->

            <!-- Checkbox transfer score -->
            <div
              class="
                md-layout-item md-size-33 md-small-size-100
                flex
                items-center
              "
              v-if="selectedLMSName === 'sis'"
            >
              <md-checkbox
                v-model="form.is_transfer_school"
                :disabled="isDetailPage"
                >{{ $t("class.transfer_score") }}
              </md-checkbox>
            </div>
            <!-- Checkbox transfer score -->

            <!-- School name -->
            <div
              class="md-layout-item md-size-66 md-small-size-100"
              v-if="form.is_transfer_school"
            >
              <validation-field
                :validations="{ required: true }"
                name="School name"
              >
                <label>{{ $t("class.school_name") }}</label>
                <md-input
                  required
                  v-model.trim="form.transfer_school_information"
                  maxlength="255"
                ></md-input>
              </validation-field>
            </div>
            <!-- School name -->

            <!-- Note -->
            <div
              class="md-layout-item md-size-100 md-small-size-100"
              v-if="form.is_transfer_school"
            >
              <label class="text-[#aaa]">{{ $t("student.note") }}</label>
              <div class="border border-gray-300 pb-2">
                <md-field>
                  <md-textarea
                    v-model.trim="form.transfer_school_note"
                  ></md-textarea>
                </md-field>
              </div>
            </div>
            <!-- Note -->
          </div>
        </md-card-content>
      </md-card>
    </div>

    <slot name="footer-actions"></slot>
  </validation-form>
</template>

<script>
import { status as statusOpts } from "@/pages/Classes/data";
import { service_paths, cms_service_paths } from "@/constants/Services";
import pick from "lodash/pick";
import cloneDeep from "lodash/cloneDeep";
import { getId, initLoader } from "@/helpers/utils";
import { Class } from "@/constants/Permissions";
import SelectFilter from "../../../components/Base/SelectFilter";
import {SYSTEM } from "@/constants/System";

const EXTERNAL_LMS = {
  agilix: "agilix",
  edmentum: "edmentum",
  lms: "IGS-LMS"
};
const DEFAULT_SYSTEM = 3;

function setEndDate() {
  const today = new Date();
  const endDate = today.setDate(today.getDate() + 1);
  return new Date(endDate).toISOString().slice(0, 10);
}

const initFormData = () => ({
  name: null,
  term_id: null,
  status: statusOpts[0].id,
  start_date: new Date().toISOString().slice(0, 10),
  end_date: setEndDate(),
  course_id: null,
  zone: null,
  lms_id: DEFAULT_SYSTEM,
  subject_id: null,
  description: null,
  is_transfer_school: false,
  transfer_school_information: null,
  transfer_school_note: null
});

export default {
  name: "main-information",
  components: { SelectFilter },
  props: ["isDetailPage", "serverValidations"],
  data() {
    return {
      form: initFormData(),
      defaultForm: initFormData(),
      termOpts: [],
      lmsCourseNameOpts: [],
      lmsZoneOpts: [],
      lmsOpts: [],
      statusOpts,
      classId: this.$route.params.id,
      courseDisplayValue: "",
      zoneDisplayValue: "",
      lmsName: "",
      subject: {
        label: "class.subject",
        value: {},
        displayValue: null,
        options: []
      },
      allowEditing: false,
      Class,
      lmsCourseNameOptsAllByZone: []
    };
  },
  created() {
    this.getTermOpts();
    this.getLMSOpts();
  },
  watch: {
    "subject.value"(newValue) {
      this.form.subject_id = newValue?.id;
      this.subject.displayValue = newValue;
    },
    "form.name"() {
      this.serverValidations.name && delete this.serverValidations.name;
    },
    "form.lms_id": async function(newLMSId) {
      if (newLMSId) {
        const loader = initLoader();
        await this.getLMSZoneOpts(newLMSId),
        await this.getLMSCourseOpts(newLMSId, {zoneId: this.form.zone})
        loader.hide();
      }

      if (!this.isDetailPage || this.allowEditing) {
        this.zoneDisplayValue = null;
        this.courseDisplayValue = null;
        this.form.is_transfer_school = false;
      }
    },
    "form.is_transfer_school": function(value) {
      if (!value) this.form.transfer_school_information = null;
      this.form.transfer_school_note = null;
    }
  },
  computed: {
    selectedLMSName() {
      if (!this.form.lms_id) return "";

      // if is add new page
      const selectedLmsSystem = this.lmsOpts.find(
        lms => lms.id === this.form.lms_id
      );

      return selectedLmsSystem?.name;
    },

    isExternalLMS() {
      return Object.values(EXTERNAL_LMS).includes(this.selectedLMSName);
    },

    invalidReleaseHireDateMsg() {
      const startDate = new Date(this.form.start_date);
      const endDate = new Date(this.form.end_date);

      return startDate > endDate
        ? this.$t("class.the_end_date_must_be_a_date_after_start_date")
        : "";
    },

    canEditClassInfo() {
      return this.$can(Class.actions.edit, Class.subject);
    }
  },
  methods: {
    async filterLMSCourseOpts(lms_id, queryObj){
    if(!queryObj)
      this.lmsCourseNameOpts =this.lmsCourseNameOptsAllByZone.map(x => x)
    else
      this.lmsCourseNameOpts = this.lmsCourseNameOptsAllByZone.filter(x => x.name.includes(queryObj))
    },
    selectedZone(value){
      if(this.selectedLMSName === EXTERNAL_LMS.lms){
        this.getLMSCourseOpts(4, {zoneId: value})
      }
    },
    getValidationClass(fieldName) {
      return {
        "md-invalid": !!this.serverValidations[fieldName]
      };
    },

    getTitleCourseName() {
      if (this.selectedLMSName === EXTERNAL_LMS.agilix) {
        return this.$t("class.agilixbuzzs_course_name");
      }

      if (this.selectedLMSName === EXTERNAL_LMS.edmentum) {
        return this.$t("class.edmentumns_course_name");
      }
      
      if (this.selectedLMSName === EXTERNAL_LMS.lms) {
        return this.$t("class.lms_course");
      }
    },

    getTitleZone() {
      if (this.selectedLMSName === EXTERNAL_LMS.agilix) {
        return this.$t("class.agilixbuzzsubs_domain");
      }

      if (this.selectedLMSName === EXTERNAL_LMS.edmentum) {
        return this.$t("class.edmentumns_program");
      }
      
      if (this.selectedLMSName === EXTERNAL_LMS.lms) {
        return this.$t("class.lms_program");
      }
    },

    submitForm() {
      if (!!this.invalidReleaseHireDateMsg) return;
      const submittingFormData = this.getSubmitForm();

      this.$emit("on-submit", submittingFormData);
    },

    updateForm(classInfo) {
      this.form = pick(classInfo, Object.keys(this.form));
    },

    clearForm() {
      this.updateForm(this.defaultForm);
    },

    getSubmitForm() {
      const formData = cloneDeep(this.form);
      // not update these fields
      if (this.isDetailPage && !this.allowEditing) {
        delete formData.lms_id;
        delete formData.course_id;
        delete formData.zone;
      }
      return formData;
    },

    // called when update in detail page
    injectFormData(classInfo) {
      this.updateForm(classInfo);
      this.subject.value = classInfo.subject;

      // inital default values
      this.defaultForm = pick(classInfo, Object.keys(this.defaultForm));

      if (this.isDetailPage) {
        this.courseDisplayValue = classInfo.course?.name;
      }

      if (classInfo.status === "Pending" && classInfo.lms_id === null) {
        this.allowEditing = true;
      }
    },

    getTermOpts() {
      this.$axios
        .get(service_paths.terms, { limit: 10000 })
        .then(({ data: resData }) => {
          this.termOpts = resData?.data;
        });
    },

    getLMSOpts() {
      return this.$axios.get(service_paths.lms).then(({ data: resData }) => {
        this.lmsOpts = resData?.data;
      });
    },

    fetchSubjectOptions(searchKey) {
      return this.$axios
        .get(
          `${service_paths.subjects}`,
          { status: "Active", "subject_display_name__~": searchKey, limit: 50 },
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(response => {
          let options = [];
          response.data.data.map(subject => {
            options.push({
              id: getId(subject),
              name: subject.subject_display_name || subject.name
            });
          });
          this.subject.options = options;
        });
    },

    async getLMSCourseOpts(lmsSystemId, queryObj = {}) {
      if(lmsSystemId === 4){
        let {data: {data: resData}} = await this.$axios.cms_post(
          `${cms_service_paths.courses}`,
          {
            "id": queryObj.zoneId
          },
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )

        this.lmsCourseNameOptsAllByZone = (resData || {}).total_courses || [];
        this.courseDisplayValue = (this.lmsCourseNameOptsAllByZone.find(x => x.id === this.form.course_id) || {}).name
        this.lmsCourseNameOpts = this.lmsCourseNameOptsAllByZone.map(x => x)
        // reset selected
        // this.form.course_id = null;
      }
      else{
        return this.$axios
        .get(
          `${service_paths.lms}/${lmsSystemId}/courses`,
          queryObj,
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(({ data: resData }) => {
          this.lmsCourseNameOpts = resData?.data || [];

          // reset selected
          this.form.course_id = null;
        });
      }
  
    },

    async getLMSZoneOpts(lmsSystemId) {
      if(lmsSystemId === 4){
        let { data: {data : resData} } = await this.$axios
          .cms_post(
            `${cms_service_paths.program_learning}`,
            {
              "page": 1,
              "rowsPerPage": 1000000,
              "colfiler": {
                "status": "activated"
              }
            },
            true,
            undefined,
            undefined,
            undefined,
            false
          )

        this.lmsZoneOpts = resData.map(x => {
            return {
              title: `${x.school_year_name} ${x.term_name} ${x.program_name}`,
              id: x.id
            }
          });
;
          if (this.isDetailPage) {
            const data = this.lmsZoneOpts.find(
              zone => +zone.id === +this.form.zone
            );
            this.zoneDisplayValue = data?.title;
          }

          // reset selected
          // this.form.zone = null;
      }
      else{
        return this.$axios
        .get(
          `${service_paths.lms}/${lmsSystemId}/zones`,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(({ data: resData }) => {
          this.lmsZoneOpts = resData;

          if (this.isDetailPage) {
            const data = this.lmsZoneOpts.find(
              zone => +zone.id === +this.form.zone
            );
            this.zoneDisplayValue = data?.title;
          }

          // reset selected
          this.form.zone = null;
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.creating-class-mode {
  padding-bottom: 14px;
}

.md-error {
  color: red;
  font-size: 12px;
}

::v-deep .md-checkbox.md-disabled .md-checkbox-label {
  opacity: 0.6;
}
</style>
